import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Users2, MapPin, Briefcase, X, Linkedin, Globe, Mail, Star, ArrowRight, Clock, LayoutGrid, List, Sparkles, FileText, ShieldCheck, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { trainerInitials, resolveTrainerImage } from "@/lib/trainers";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] } }),
};

const PAGE_SIZE = 9;
const SORTS = { featured: "Featured First", latest: "Latest", alpha: "Alphabetical", experience: "Most Experienced" };

// Experience strings aren't numeric ("5-10 Years") — parse the first number found so
// "Most Experienced" sort has something real to compare, rather than a string sort that
// would put "10+ Years" before "2-5 Years".
const experienceValue = (e) => parseInt((e.experience || "").match(/\d+/)?.[0] || "0", 10);

export default function Experts() {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("All");
  const [location, setLocation] = useState("All");
  const [skillFilter, setSkillFilter] = useState("All");
  const [experienceFilter, setExperienceFilter] = useState("All");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sort, setSort] = useState("featured");
  const [view, setView] = useState(() => localStorage.getItem("experts_view") || "grid");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [related, setRelated] = useState([]);
  const [statusModal, setStatusModal] = useState(false);
  const [cms, setCms] = useState(null);

  const loadExperts = () => {
    setLoading(true);
    setLoadError(false);
    api.get("/experts")
      .then((r) => setExperts(r.data || []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadExperts();
    api.get("/cms/experts-page").then((r) => setCms(r.data)).catch(() => setCms(null));
  }, []);

  const setViewMode = (v) => { setView(v); localStorage.setItem("experts_view", v); };

  useEffect(() => {
    if (!selected) { setRelated([]); return; }
    api.get(`/experts/${selected.id}/related`).then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [selected]);

  const subjects = useMemo(() => ["All", ...Array.from(new Set(experts.map((e) => e.subject_expertise).filter(Boolean)))], [experts]);
  const locations = useMemo(() => ["All", ...Array.from(new Set(experts.map((e) => e.state).filter(Boolean)))], [experts]);
  const allSkills = useMemo(() => ["All", ...Array.from(new Set(experts.flatMap((e) => e.skills || [])))], [experts]);
  const experienceBands = ["All", "Under 2 Years", "2-5 Years", "5-10 Years", "10+ Years"];

  const experienceInBand = (e, band) => {
    if (band === "All") return true;
    const v = experienceValue(e);
    if (band === "Under 2 Years") return v < 2;
    if (band === "2-5 Years") return v >= 2 && v <= 5;
    if (band === "5-10 Years") return v > 5 && v <= 10;
    if (band === "10+ Years") return v > 10;
    return true;
  };

  const filtered = useMemo(() => {
    let list = experts.filter((e) =>
      (subject === "All" || e.subject_expertise === subject) &&
      (location === "All" || e.state === location) &&
      (skillFilter === "All" || (e.skills || []).includes(skillFilter)) &&
      experienceInBand(e, experienceFilter) &&
      (!featuredOnly || e.featured) &&
      (!q || `${e.full_name} ${e.skills?.join(" ")} ${e.subject_expertise}`.toLowerCase().includes(q.toLowerCase()))
    );
    list = [...list];
    if (sort === "featured") list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || a.full_name.localeCompare(b.full_name));
    else if (sort === "latest") list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else if (sort === "alpha") list.sort((a, b) => a.full_name.localeCompare(b.full_name));
    else if (sort === "experience") list.sort((a, b) => experienceValue(b) - experienceValue(a));
    return list;
  }, [experts, q, subject, location, skillFilter, experienceFilter, featuredOnly, sort]);

  useEffect(() => { setPage(1); }, [q, subject, location, skillFilter, experienceFilter, featuredOnly, sort]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Real quick stats computed from the same approved-experts data already fetched — not
  // hardcoded, and no separate backend call needed for numbers that are already in memory.
  const heroStats = useMemo(() => {
    const subjectCount = new Set(experts.map((e) => e.subject_expertise).filter(Boolean)).size;
    const schoolCount = new Set(experts.map((e) => e.school).filter(Boolean)).size;
    const totalYears = experts.reduce((sum, e) => sum + experienceValue(e), 0);
    return [
      { label: "Approved Experts", value: experts.length },
      { label: "Subject Areas", value: subjectCount },
      { label: "Years of Experience", value: totalYears ? `${totalYears}+` : "—" },
      { label: "Schools Represented", value: schoolCount },
    ];
  }, [experts]);

  return (
    <div style={{ backgroundColor: "#F8FAFC" }} data-testid="experts-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,104,231,0.08),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(69,184,172,0.08),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-14">
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0} className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6" style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}>
            <Users2 className="w-3.5 h-3.5" /> {cms?.hero_badge || "Expert Network"}
          </motion.div>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1} className="rebrand-serif tracking-tight font-bold leading-[1.05]" style={{ fontSize: "clamp(2.5rem, 5vw + 1rem, 4rem)", color: "#1A202C" }} data-testid="experts-headline">
            {cms?.hero_title || "Educators shaping the future of teaching."}
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-6 text-base sm:text-lg leading-relaxed max-w-2xl" style={{ color: "#64748B" }}>
            {cms?.hero_subtitle || "Verified professionals from schools across the country, ready to share their expertise."}
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#directory" data-testid="experts-browse-cta"
              onClick={(e) => { e.preventDefault(); document.getElementById("directory")?.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-2 text-white text-sm font-semibold transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px", boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}
            >
              Browse Experts <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/experts/apply" data-testid="experts-apply-cta" className="inline-flex items-center gap-2 text-sm font-medium" style={{ border: "2px solid #DDA756", color: "#DDA756", borderRadius: 50, padding: "10px 22px" }}>
              {cms?.hero_cta_text || "Become an Expert"}
            </Link>
            <button onClick={() => setStatusModal(true)} data-testid="experts-check-status" className="text-sm font-medium hover:underline" style={{ color: "#64748B" }}>
              Check application status
            </button>
          </motion.div>

          {!loading && experts.length > 0 && (
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {heroStats.map((s, i) => (
                <div key={i}>
                  <div className="rebrand-serif text-2xl font-bold" style={{ color: "#1A202C" }}>{s.value}</div>
                  <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>{s.label}</div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Application Process — CMS-driven steps, reuses the same fadeUp/stagger pattern as
          every other section on this page */}
      {cms?.process_steps?.some((s) => s.active !== false) && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-14">
          <h2 className="rebrand-serif text-xl sm:text-2xl font-bold mb-6 text-center" style={{ color: "#1A202C" }}>{cms?.process_heading || "How It Works"}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {[...cms.process_steps].filter((s) => s.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).map((s, i) => {
              const Icon = { FileText, Search, ShieldCheck, CheckCircle2, Sparkles }[s.icon] || FileText;
              return (
                <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="text-center">
                  <div className="w-12 h-12 rounded-full grid place-items-center mx-auto mb-3" style={{ backgroundColor: "#3B68E714" }}>
                    <Icon className="w-5 h-5" style={{ color: "#3B68E7" }} />
                  </div>
                  <div className="font-semibold text-sm" style={{ color: "#1A202C" }}>{s.title}</div>
                  <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "#64748B" }}>{s.description}</p>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* Why Join — CMS-driven cards */}
      {cms?.why_join_cards?.some((c) => c.active !== false) && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-14">
          <h2 className="rebrand-serif text-xl sm:text-2xl font-bold mb-2" style={{ color: "#1A202C" }}>{cms?.why_join_heading || "Why Join"}</h2>
          {cms?.why_join_description && <p className="text-sm mb-6" style={{ color: "#64748B" }}>{cms.why_join_description}</p>}
          <div className="grid sm:grid-cols-3 gap-5">
            {[...cms.why_join_cards].filter((c) => c.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).map((c, i) => (
              <div key={i} className="bg-white p-5" style={{ borderRadius: 20, border: "1px solid #E2E8F0" }}>
                <div className="w-9 h-9 rounded-lg grid place-items-center mb-3" style={{ backgroundColor: "#3B68E714" }}>
                  <Sparkles className="w-4 h-4" style={{ color: "#3B68E7" }} />
                </div>
                <div className="font-semibold text-sm" style={{ color: "#1A202C" }}>{c.title}</div>
                <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "#64748B" }}>{c.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Featured Experts — reuses the same experts array already fetched for the
          directory below; no separate API call. */}
      {!loading && experts.some((e) => e.featured) && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-14">
          <h2 className="rebrand-serif text-xl sm:text-2xl font-bold mb-6" style={{ color: "#1A202C" }}>{cms?.featured_heading || "Featured Experts"}</h2>
          <div className="flex gap-5 overflow-x-auto pb-2 no-scrollbar">
            {experts.filter((e) => e.featured).map((ex) => (
              <div key={ex.id} className="shrink-0 w-64 bg-white p-5" style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }} data-testid={`featured-expert-${ex.id}`}>
                <div className="flex items-center gap-3">
                  {ex.photo ? (
                    <img src={resolveTrainerImage(ex.photo)} alt={ex.full_name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full grid place-items-center text-white font-semibold" style={{ backgroundColor: "#45B8AC" }}>{trainerInitials(ex.full_name)}</div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1" style={{ color: "#1A202C" }}>{ex.full_name} <Star className="w-3 h-3 shrink-0" style={{ color: "#DDA756", fill: "#DDA756" }} /></div>
                    <div className="text-xs truncate" style={{ color: "#64748B" }}>{ex.subject_expertise}</div>
                  </div>
                </div>
                <button onClick={() => setSelected(ex)} className="mt-4 text-xs font-semibold inline-flex items-center gap-1" style={{ color: "#3B68E7" }}>
                  View Profile <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section id="directory" className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl" style={{ borderBottom: "1px solid #E2E8F0" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, skill…" data-testid="experts-search" className="w-full pl-10 pr-3.5 py-2.5 outline-none text-sm bg-white" style={{ borderRadius: 50, border: "1px solid #E2E8F0" }} />
          </div>
          <select value={subject} onChange={(e) => setSubject(e.target.value)} className="px-3 py-2 rounded-full text-xs font-medium bg-white" style={{ border: "1px solid #E2E8F0" }}>
            {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={location} onChange={(e) => setLocation(e.target.value)} className="px-3 py-2 rounded-full text-xs font-medium bg-white" style={{ border: "1px solid #E2E8F0" }}>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={skillFilter} onChange={(e) => setSkillFilter(e.target.value)} className="px-3 py-2 rounded-full text-xs font-medium bg-white" style={{ border: "1px solid #E2E8F0" }} data-testid="experts-skill-filter">
            {allSkills.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={experienceFilter} onChange={(e) => setExperienceFilter(e.target.value)} className="px-3 py-2 rounded-full text-xs font-medium bg-white" style={{ border: "1px solid #E2E8F0" }} data-testid="experts-experience-filter">
            {experienceBands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-full text-xs font-medium bg-white" style={{ border: "1px solid #E2E8F0" }} data-testid="experts-sort">
            {Object.entries(SORTS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <button
            onClick={() => setFeaturedOnly((v) => !v)}
            className="px-3 py-2 rounded-full text-xs font-medium inline-flex items-center gap-1.5"
            style={{ border: `1px solid ${featuredOnly ? "#DDA756" : "#E2E8F0"}`, backgroundColor: featuredOnly ? "#DDA75714" : "#FFFFFF", color: featuredOnly ? "#B8863E" : "#374151" }}
          >
            <Star className="w-3 h-3" style={featuredOnly ? { fill: "#DDA756", color: "#DDA756" } : {}} /> Featured
          </button>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:block text-xs font-medium" style={{ color: "#64748B" }}>{filtered.length} experts</span>
            <div className="flex rounded-full overflow-hidden" style={{ border: "1px solid #E2E8F0" }}>
              <button onClick={() => setViewMode("grid")} aria-label="Grid view" className="p-2" style={{ backgroundColor: view === "grid" ? "#1A202C" : "#FFFFFF", color: view === "grid" ? "#FFFFFF" : "#64748B" }}><LayoutGrid className="w-3.5 h-3.5" /></button>
              <button onClick={() => setViewMode("list")} aria-label="List view" className="p-2" style={{ backgroundColor: view === "list" ? "#1A202C" : "#FFFFFF", color: view === "list" ? "#FFFFFF" : "#64748B" }}><List className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16">
        {loadError ? (
          <div className="p-16 text-center" style={{ borderRadius: 24, border: "1px solid #FECACA", backgroundColor: "#FEF2F2" }} data-testid="experts-error-state">
            <X className="w-8 h-8 mx-auto mb-3" style={{ color: "#DC2626" }} />
            <div className="rebrand-serif text-xl font-semibold" style={{ color: "#1A202C" }}>Couldn&apos;t load experts.</div>
            <p className="mt-2 text-sm" style={{ color: "#64748B" }}>Something went wrong on our end. Please try again.</p>
            <button
              onClick={loadExperts} data-testid="experts-retry"
              className="mt-4 inline-flex items-center gap-1.5 text-white text-sm font-semibold"
              style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "10px 20px" }}
            >
              Retry
            </button>
          </div>
        ) : loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-64 bg-white animate-pulse" style={{ borderRadius: 24, border: "1px solid #E2E8F0" }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" style={{ borderRadius: 24, border: "2px dashed #E2E8F0" }}>
            <Users2 className="w-8 h-8 mx-auto mb-3" style={{ color: "#CBD5E1" }} />
            <div className="rebrand-serif text-xl font-semibold" style={{ color: "#1A202C" }}>No experts match yet.</div>
            <p className="mt-2 text-sm" style={{ color: "#64748B" }}>Be the first — apply to join the network.</p>
            <Link to="/experts/apply" className="mt-4 inline-flex items-center gap-1.5 text-white text-sm font-semibold transition-transform hover:scale-[1.02]" style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "10px 20px" }}>
              Apply to Join <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <>
            <div className={view === "grid" ? "grid sm:grid-cols-2 lg:grid-cols-3 gap-6" : "grid gap-4"}>
              {paged.map((ex, i) => (
                <motion.button
                  key={ex.id} onClick={() => setSelected(ex)} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i % 6}
                  className={`text-left bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_35px_rgba(0,0,0,0.08)] ${view === "grid" ? "p-6" : "p-4 flex items-center gap-4"}`}
                  style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
                  data-testid={`expert-card-${ex.id}`}
                >
                  <div className={view === "grid" ? "flex items-center gap-3" : "flex items-center gap-3 shrink-0"}>
                    {ex.photo ? (
                      <img src={resolveTrainerImage(ex.photo)} alt={ex.full_name} className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full grid place-items-center text-white font-semibold shrink-0" style={{ backgroundColor: "#45B8AC" }}>{trainerInitials(ex.full_name)}</div>
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold truncate flex items-center gap-1.5" style={{ color: "#1A202C" }}>
                        {ex.full_name}
                        {ex.featured && <Star className="w-3.5 h-3.5 shrink-0" style={{ color: "#DDA756", fill: "#DDA756" }} />}
                    </div>
                    <div className="text-xs truncate" style={{ color: "#64748B" }}>{ex.designation}</div>
                  </div>
                  </div>
                  <div className={view === "list" ? "flex-1 min-w-0" : ""}>
                  <div className="mt-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}>
                    {ex.subject_expertise}
                  </div>
                  {ex.bio && <p className="mt-3 text-sm line-clamp-2 leading-relaxed" style={{ color: "#64748B" }}>{ex.bio}</p>}
                  <div className="mt-4 flex items-center gap-3 text-xs" style={{ color: "#94A3B8" }}>
                    {ex.school && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {ex.school}</span>}
                    {ex.state && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {ex.state}</span>}
                  </div>
                  </div>
              </motion.button>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p} onClick={() => setPage(p)}
                    className="w-8 h-8 rounded-full text-xs font-medium transition-colors"
                    style={{ backgroundColor: p === page ? "#1A202C" : "#FFFFFF", color: p === page ? "#FFFFFF" : "#64748B", border: "1px solid #E2E8F0" }}
                    data-testid={`experts-page-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* Profile modal */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: 28 }} onClick={(e) => e.stopPropagation()} data-testid="expert-profile-modal">
              {/* Cover banner */}
              <div className="relative h-28" style={{ background: "linear-gradient(135deg, #3B68E7 0%, #2947B0 100%)", borderRadius: "28px 28px 0 0" }}>
                <button onClick={() => setSelected(null)} aria-label="Close" className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 text-white">
                  <X className="w-4 h-4" />
                </button>
                {selected.featured && (
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ backgroundColor: "#DDA756", color: "#1A202C" }}>
                    <Star className="w-3 h-3 fill-current" /> Featured
                  </span>
                )}
              </div>

              <div className="px-6 sm:px-8 pb-8">
                <div className="-mt-10 flex items-end gap-4">
                  {selected.photo ? (
                    <img src={resolveTrainerImage(selected.photo)} alt={selected.full_name} className="w-20 h-20 rounded-full object-cover border-4 border-white" />
                  ) : (
                    <div className="w-20 h-20 rounded-full grid place-items-center text-white font-semibold text-xl border-4 border-white" style={{ backgroundColor: "#45B8AC" }}>{trainerInitials(selected.full_name)}</div>
                  )}
                </div>

                <div className="mt-3">
                  <h2 className="rebrand-serif text-xl sm:text-2xl font-bold" style={{ color: "#1A202C" }}>{selected.full_name}</h2>
                  <div className="text-sm mt-0.5" style={{ color: "#64748B" }}>{selected.designation}{selected.school && ` · ${selected.school}`}</div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs" style={{ color: "#94A3B8" }}>
                    {selected.experience && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {selected.experience}</span>}
                    {(selected.city || selected.state) && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {[selected.city, selected.state].filter(Boolean).join(", ")}</span>}
                  </div>
                </div>

                {selected.bio && <p className="mt-5 text-sm leading-relaxed" style={{ color: "#374151" }}>{selected.bio}</p>}

                <div className="mt-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}>
                  {selected.subject_expertise}
                </div>

                <ChipGroup label="Skills" items={selected.skills} color="#3B68E7" bg="#3B68E714" />
                <ChipGroup label="Qualifications" items={selected.qualifications} color="#3FB39E" bg="#3FB39E14" />
                <ChipGroup label="Languages" items={selected.languages} color="#B8863E" bg="#DDA75614" />
                <ChipGroup label="Achievements" items={selected.achievements} color="#374151" bg="#F1F5F9" />

                {selected.previous_schools?.length > 0 && (
                  <div className="mt-5">
                    <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "#64748B" }}>Previous Schools</div>
                    <ul className="text-sm space-y-1" style={{ color: "#374151" }}>
                      {selected.previous_schools.map((s, i) => <li key={i}>· {s}</li>)}
                    </ul>
                  </div>
                )}

                <div className="mt-6 pt-5 flex flex-wrap items-center gap-3" style={{ borderTop: "1px solid #E2E8F0" }}>
                  {selected.linkedin && <a href={selected.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "#3B68E7" }}><Linkedin className="w-4 h-4" /> LinkedIn</a>}
                  {selected.website && <a href={selected.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "#3B68E7" }}><Globe className="w-4 h-4" /> Website</a>}
                  {selected.email && <a href={`mailto:${selected.email}`} className="ml-auto inline-flex items-center gap-1.5 text-white text-sm font-semibold" style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "8px 18px" }}><Mail className="w-4 h-4" /> Contact</a>}
                </div>

              {related.length > 0 && (
                <div className="mt-6 pt-5" style={{ borderTop: "1px solid #E2E8F0" }}>
                  <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "#64748B" }}>Related Experts</div>
                  <div className="grid grid-cols-3 gap-3">
                    {related.map((r) => (
                      <button key={r.id} onClick={() => setSelected(r)} className="text-left p-2.5 rounded-xl hover:bg-gray-50">
                        {r.photo ? (
                          <img src={resolveTrainerImage(r.photo)} alt={r.full_name} className="w-10 h-10 rounded-full object-cover mb-1.5" />
                        ) : (
                          <div className="w-10 h-10 rounded-full grid place-items-center text-white text-xs font-semibold mb-1.5" style={{ backgroundColor: "#45B8AC" }}>{trainerInitials(r.full_name)}</div>
                        )}
                        <div className="text-xs font-medium truncate" style={{ color: "#1A202C" }}>{r.full_name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <StatusCheckModal open={statusModal} onClose={() => setStatusModal(false)} />
    </div>
  );
}

const STATUS_LABELS = {
  pending: "Pending review",
  approved: "Approved — you're on the directory!",
  rejected: "Not approved",
  changes_requested: "Changes requested — check your email",
};

function StatusCheckModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    setError(""); setResult(null); setLoading(true);
    try {
      const r = await api.get("/experts/applications/status/check", { params: { email, phone } });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not check status");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-sm p-6" style={{ borderRadius: 24 }} onClick={(e) => e.stopPropagation()} data-testid="expert-status-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="rebrand-serif text-lg font-bold" style={{ color: "#1A202C" }}>Check application status</h2>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center rounded-full hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={check} className="grid gap-3">
          <input required type="email" placeholder="Email used in application" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none" style={{ borderColor: "#E2E8F0" }} data-testid="status-email" />
          <input required placeholder="Phone number used in application" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none" style={{ borderColor: "#E2E8F0" }} data-testid="status-phone" />
          <button type="submit" disabled={loading} className="text-white text-sm font-semibold py-2.5 rounded-full" style={{ backgroundColor: "#3B68E7" }} data-testid="status-check-submit">
            {loading ? "Checking…" : "Check Status"}
          </button>
        </form>
        {error && <p className="mt-3 text-xs text-rose-600">{error}</p>}
        {result && (
          <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: "#F8FAFC" }}>
            <div className="text-sm font-semibold" style={{ color: "#1A202C" }}>{result.full_name}</div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-sm" style={{ color: "#3B68E7" }}>
              <Clock className="w-3.5 h-3.5" /> {STATUS_LABELS[result.status] || result.status}
            </div>
            {result.status === "changes_requested" && (
              <>
                {result.admin_notes && <p className="mt-2 text-xs" style={{ color: "#64748B" }}>{result.admin_notes}</p>}
                <a
                  href={`/experts/apply?resubmit=1&email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-white text-xs font-semibold"
                  style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "8px 16px" }}
                >
                  Update &amp; Resubmit
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ChipGroup({ label, items, color, bg }) {
  if (!items?.length) return null;
  return (
    <div className="mt-5">
      <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "#64748B" }}>{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: bg, color }}>{s}</span>)}
      </div>
    </div>
  );
}
